import { useEffect } from "react";
import { Mail, ArrowUpRight, LifeBuoy } from "lucide-react";
import { PageHeader } from "@/components/landing/PageHeader";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { TOPICS, SUPPORT_EMAIL, mailto } from "./supportContent";

const ContactCard = () => (
  <div className="rounded-3xl bg-[#FF3838] p-6 sm:p-8 text-[#0A0B0E]" data-testid="support-contact-card">
    <Mail className="h-6 w-6" />
    <p className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-[#0A0B0E]/70">Email support</p>
    <a href={mailto("Support request")} data-testid="support-email-link"
      className="mt-2 block break-all font-display text-[15px] sm:text-xl font-bold underline-offset-4 hover:underline">
      {SUPPORT_EMAIL}
    </a>
    <p className="mt-4 text-sm text-[#0A0B0E]/80">Tell us what happened, your username, and include screenshots where possible.</p>
  </div>
);

const Topic = ({ t, i }) => (
  <Reveal y={16} delay={(i % 2) * 0.06}>
    <section id={t.id} className="scroll-mt-28 h-full rounded-3xl border border-white/10 bg-[#12141C] p-6 sm:p-8 flex flex-col" data-testid={`support-topic-${t.id}`}>
      <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight">{t.title}</h2>
      <ul className="mt-4 flex-1 space-y-3 text-sm text-zinc-300 leading-relaxed list-disc pl-5 marker:text-[#FF3838]">
        {t.points.map((p, j) => <li key={j}>{p}</li>)}
      </ul>
      <a href={mailto(t.subject)} data-testid={`support-email-${t.id}`}
        className="group mt-6 inline-flex w-fit items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm font-medium transition-colors hover:border-[#FF3838] hover:text-[#FF3838]">
        Email us about this <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
      </a>
    </section>
  </Reveal>
);

export default function Support() {
  useEffect(() => {
    document.title = "Help & Support | UrgentSellBuy";
    window.scrollTo(0, 0);
    return () => { document.title = "UrgentSellBuy | Sell it today. Buy it first."; };
  }, []);

  return (
    <>
      <PageHeader />
      <main className="mx-auto max-w-7xl px-5 sm:px-8 pt-32 pb-24" data-testid="support-page">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <Eyebrow testId="support-eyebrow"><LifeBuoy className="h-3.5 w-3.5" /> Help & Support</Eyebrow>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95]" data-testid="support-title">
              How can we help?
            </h1>
            <p className="mt-6 max-w-lg text-zinc-300">Find help with your account, listings, chat, payments and account deletion. Can't find what you need? Email us.</p>
          </div>
          <div className="lg:col-span-5"><ContactCard /></div>
        </div>
        <div className="mt-16 grid md:grid-cols-2 gap-4">
          {TOPICS.map((t, i) => <Topic key={t.id} t={t} i={i} />)}
        </div>
      </main>
    </>
  );
}
